// @ts-nocheck
/* assets/wishlist.js
   - Wishlist localStorage (dwell:wishlist:v2)
   - Real-time Wishlist Drawer with smooth slide-out and instant product listing
   - Full Wishlist page rendering via section_id=wishlist-card
   - Dynamic 404 fallback for /pages/wishlist so it ALWAYS opens
   - Heart toggle synchronization across all cards and PDP
   - Brand wine-red counter badge with strict zero-hiding (no blank circle)
   - Move to cart integration with cart-drawer-component
   - Toast notification system
   - Shareable wishlist URLs (?wl=handle1,handle2)
*/

(() => {
  const KEY = "dwell:wishlist:v2";
  const EVENT = "wishlist:changed";

  // ---------------------------
  // Storage
  // ---------------------------
  const safeJson = (raw) => {
    try { return JSON.parse(raw); } catch (e) { return null; }
  };

  const read = () => {
    const arr = safeJson(localStorage.getItem(KEY));
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  };

  const write = (items) => {
    const clean = Array.from(new Set((items || []).filter(Boolean)));
    localStorage.setItem(KEY, JSON.stringify(clean));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { items: clean } }));
    return clean;
  };

  const removeHandle = (handle) => write(read().filter((h) => h !== handle));

  const toggleHandle = (handle) => {
    const items = read();
    if (items.includes(handle)) {
      return { items: write(items.filter((h) => h !== handle)), action: "removed" };
    }
    return { items: write([...items, handle]), action: "added" };
  };

  // ---------------------------
  // Toast
  // ---------------------------
  let toastEl = null;
  let toastTimer = null;

  const ensureToast = () => {
    if (toastEl) return toastEl;
    toastEl = document.createElement("div");
    toastEl.className = "wishlist-toast";
    toastEl.setAttribute("role", "status");
    toastEl.setAttribute("aria-live", "polite");
    document.body.appendChild(toastEl);
    return toastEl;
  };

  const showToast = (msg) => {
    const el = ensureToast();
    el.textContent = msg;
    el.classList.remove("is-visible");
    void el.offsetWidth;
    el.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => el.classList.remove("is-visible"), 1400);
  };

  // ---------------------------
  // Currency Formatter Helper
  // ---------------------------
  const formatMoney = (cents) => {
    if (typeof cents !== "number") return "";
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents);
      }
    } catch (e) {}
    const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "INR";
    return (cents / 100).toLocaleString(undefined, {
      style: "currency",
      currency: activeCurrency,
    });
  };

  // ---------------------------
  // Sync UI (hearts + wishlist count)
  // ---------------------------
  const setBtnState = (btn, active) => {
    btn.classList.toggle("is-active", !!active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
  };

  const syncWishlistBadge = (items) => {
    const count = items.length;

    // Update inline text count for desktop e.g. Wishlist (0)
    document.querySelectorAll("[data-wishlist-count-inline]").forEach((el) => {
      el.textContent = String(count);
    });

    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      el.setAttribute("data-count", String(count));
      if (count <= 0) {
        el.textContent = "";
        el.hidden = true;
        el.setAttribute("aria-hidden", "true");
        el.style.display = "none";
        return;
      }

      el.textContent = String(count);
      el.hidden = false;
      el.setAttribute("aria-hidden", "false");
      el.style.display = "flex";
    });
  };

  const sync = (root = document) => {
    const items = read();

    root.querySelectorAll("[data-wishlist-btn]").forEach((btn) => {
      const handle = btn.getAttribute("data-handle");
      if (!handle) return;
      setBtnState(btn, items.includes(handle));
    });

    syncWishlistBadge(items);
  };

  // ---------------------------
  // Wishlist Drawer Control
  // ---------------------------
  const getDrawer = () => document.getElementById("wishlist-drawer");

  const openWishlistDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.add("is-open");
    drawer.removeAttribute("inert");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    renderDrawerItems();
  };

  const closeWishlistDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.remove("is-open");
    drawer.setAttribute("inert", "");
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  };

  const renderDrawerItems = async () => {
    const drawer = getDrawer();
    if (!drawer) return;

    const countBadge = drawer.querySelector(".wishlist-drawer__count-badge");
    const items = read();

    if (countBadge) {
      countBadge.textContent = String(items.length);
      countBadge.hidden = items.length === 0;
    }

    if (!drawer.classList.contains("is-open")) return;

    const list = drawer.querySelector("[data-wishlist-drawer-items]");
    const empty = drawer.querySelector("[data-wishlist-empty]");
    const footer = drawer.querySelector("[data-wishlist-footer]");

    if (!items.length) {
      if (list) list.innerHTML = "";
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;
    if (!list) return;

    list.innerHTML = `<li style="text-align: center; padding: 24px; color: rgba(0,0,0,0.5);">Loading your favorites...</li>`;

    const htmlParts = await Promise.all(
      items.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!res.ok) return "";
          const prod = await res.json();

          const img = prod.featured_image ? prod.featured_image : "";
          const price = formatMoney(prod.price);

          return `
            <li class="wishlist-drawer-item" data-handle="${prod.handle}">
              <div class="wishlist-drawer-item__img-wrap">
                ${img ? `<img src="${img}" alt="${prod.title}" class="wishlist-drawer-item__img" loading="lazy" />` : ""}
              </div>
              <div class="wishlist-drawer-item__info">
                <div>
                  <a href="${prod.url}" class="wishlist-drawer-item__title">${prod.title}</a>
                  <div class="wishlist-drawer-item__price">${price}</div>
                </div>
                <div class="wishlist-drawer-item__actions">
                  <button
                    type="button"
                    class="button button--primary wishlist-drawer-item__move-btn"
                    data-wishlist-move
                    data-handle="${prod.handle}"
                  >
                    Move to Cart
                  </button>
                  <button
                    type="button"
                    class="wishlist-drawer-item__remove-btn"
                    data-wishlist-remove
                    data-handle="${prod.handle}"
                    aria-label="Remove item"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          `;
        } catch (e) {
          return "";
        }
      })
    );

    list.innerHTML = htmlParts.filter(Boolean).join("");
  };

  // Section IDs for cart-items-component
  const getCartItemsSectionIds = () =>
    Array.from(document.querySelectorAll("cart-items-component[data-section-id]"))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(",");

  // ---------------------------
  // Wishlist page render (full grid)
  // ---------------------------
  const renderWishlistPage = async () => {
    const page = document.querySelector("[data-wishlist-page]");
    if (!page) return;

    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    if (!grid) return;

    const items = read();

    if (!items.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    grid.innerHTML = `<li class="grid__item wishlist-grid-item" style="grid-column: 1 / -1; text-align: center; padding: 48px; color: rgba(0,0,0,0.5);">Loading your favorites...</li>`;

    const parts = await Promise.all(
      items.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=wishlist-card`, {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!res.ok) return "";
          const html = await res.text();

          const doc = new DOMParser().parseFromString(html, "text/html");
          const li = doc.querySelector("li.grid__item, li.wishlist-grid-item");
          return li ? li.outerHTML : "";
        } catch (e) {
          return "";
        }
      })
    );

    grid.innerHTML = parts.filter(Boolean).join("");
    sync(page);

    try {
      if (window.jdgm && typeof window.jdgm.initializeBadges === "function") {
        window.jdgm.initializeBadges();
      } else if (window.jdgm && typeof window.jdgm.badge === "function") {
        window.jdgm.badge();
      }
    } catch (e) {}
  };

  // ---------------------------
  // Dynamic 404 Fallback for /pages/wishlist
  // ---------------------------
  const check404Fallback = () => {
    const path = window.location.pathname.replace(/\/$/, "");
    if (path !== "/pages/wishlist") return;
    if (document.querySelector("[data-wishlist-page]")) return;

    const main = document.querySelector("#MainContent") || document.body;
    const target =
      main.querySelector(".template-404, .page-404, .section--404") ||
      main.querySelector("section") ||
      main;

    const templateWrap = document.createElement("div");
    templateWrap.innerHTML = `
      <div data-wishlist-page class="wishlist-page section section--page-width color-scheme-1" style="padding-top: 40px; padding-bottom: 60px; max-width: var(--page-width, 1360px); margin: 0 auto; padding-inline: 20px;">
        <div class="wishlist-page__header" style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem;">
          <h1 class="main-page-title page-title h1" style="margin: 0; font-size: 2rem;">Wishlist</h1>
          <div class="wishlist-page__actions">
            <button type="button" class="wishlist-share-btn" data-wishlist-share aria-label="Share wishlist" style="background: transparent; border: 0; cursor: pointer; padding: 8px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                <polyline points="16 6 12 2 8 6"></polyline>
                <line x1="12" y1="2" x2="12" y2="15"></line>
              </svg>
            </button>
          </div>
        </div>

        <div class="wishlist-page__empty" data-wishlist-empty hidden style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 48px 16px; margin: 0 auto; max-width: 580px;">
          <div style="width: 68px; height: 68px; border-radius: 50%; background: rgba(0,0,0,0.04); color: #872630; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 21s-7.2-4.6-9.6-8.7C.7 9 2.4 5.8 5.7 4.7c2.1-.7 4.3.1 5.7 1.8 1.4-1.7 3.6-2.5 5.7-1.8 3.3 1.1 5 4.3 3.3 7.6C19.2 16.4 12 21 12 21z"></path>
            </svg>
          </div>
          <h2 class="wishlist-page__empty-title h3" style="margin: 0 0 12px; letter-spacing: 0.04em; text-align: center;">YOUR WISHLIST IS EMPTY</h2>
          <p class="wishlist-page__empty-subtitle paragraph" style="margin: 0 auto 28px; color: rgba(0,0,0,0.65); max-width: 440px; line-height: 1.55; text-align: center;">
            Explore our curated collection and save your favorite items by tapping the heart icon.
          </p>
          <a href="/collections/all" class="button wishlist-page__empty-btn" style="min-width: 200px; max-width: 280px; width: min(100%, 260px); text-transform: uppercase; letter-spacing: 0.08em; padding: 14px 28px; display: flex; align-items: center; justify-content: center; margin: 0 auto; text-decoration: none; font-size: 0.8125rem; font-weight: 600; line-height: 1; background: #000000; color: #ffffff; border: none; outline: none; box-shadow: none; box-sizing: border-box; -webkit-tap-highlight-color: transparent;">
            EXPLORE PRODUCTS
          </a>
        </div>

        <ul class="product-grid" data-wishlist-grid product-grid-view="default" style="list-style: none; padding: 0; margin: 0;"></ul>
      </div>
    `;

    if (target !== main) {
      target.replaceWith(templateWrap.firstElementChild);
    } else {
      main.innerHTML = "";
      main.appendChild(templateWrap.firstElementChild);
    }
    document.title = "Wishlist – Agnaya";
    renderWishlistPage();
  };

  // ---------------------------
  // Variant ID Fetch & Add to Cart
  // ---------------------------
  const getFirstAvailableVariantId = async (handle) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!res.ok) throw new Error("Could not fetch product");
    const json = await res.json();
    const v = (json.variants || []).find((x) => x.available) || (json.variants || [])[0];
    if (!v) throw new Error("No variants found");
    return v.id;
  };

  const addToCart = async (variantId, qty = 1) => {
    const sectionIds = getCartItemsSectionIds();
    const payload = {
      id: variantId,
      quantity: qty,
      sections: sectionIds || undefined,
      sections_url: window.location.pathname,
    };

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  };

  // ---------------------------
  // Share URL
  // ---------------------------
  const getShareUrl = () => {
    const items = read();
    const base = `${window.location.origin}/pages/wishlist`;
    if (!items.length) return base;
    return `${base}?wl=${encodeURIComponent(items.join(","))}`;
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {}

    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch (e) {
      return false;
    }
  };

  const importFromUrlOnce = () => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("wl");
    if (!raw) return;

    const handles = raw
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    if (handles.length) {
      write([...read(), ...handles]);
    }
  };

  // ---------------------------
  // Global Click Handling
  // ---------------------------
  document.addEventListener("click", async (e) => {
    const trigger = e.target.closest("[data-wishlist-trigger]");
    const closeBtn = e.target.closest("[data-wishlist-close]");
    const btnHeart = e.target.closest("[data-wishlist-btn]");
    const btnRemove = e.target.closest("[data-wishlist-remove]");
    const btnMove = e.target.closest("[data-wishlist-move]");
    const btnShare = e.target.closest("[data-wishlist-share]");
    const btnClear = e.target.closest("[data-wishlist-clear]");

    // Navigate to wishlist page if a non-link wishlist trigger is clicked
    if (trigger && !trigger.closest("a[href]")) {
      e.preventDefault();
      window.location.href = "/pages/wishlist";
      return;
    }

    // Close Drawer
    if (closeBtn) {
      e.preventDefault();
      closeWishlistDrawer();
      return;
    }

    // Clear All
    if (btnClear) {
      e.preventDefault();
      write([]);
      showToast("Wishlist cleared");
      return;
    }

    // Heart toggle
    if (btnHeart) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnHeart.getAttribute("data-handle");
      if (!handle) return;

      const res = toggleHandle(handle);
      setBtnState(btnHeart, res.items.includes(handle));
      showToast(res.action === "added" ? "Added to wishlist" : "Removed from wishlist");
      return;
    }

    // Remove
    if (btnRemove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnRemove.getAttribute("data-handle");
      if (!handle) return;

      const itemCard = btnRemove.closest(".wishlist-drawer-item, .wishlist-grid-item");
      if (itemCard) {
        itemCard.classList.add("is-removing");
        setTimeout(() => removeHandle(handle), 200);
      } else {
        removeHandle(handle);
      }
      showToast("Removed from wishlist");
      return;
    }

    // Move to cart
    if (btnMove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnMove.getAttribute("data-handle");
      if (!handle) return;

      btnMove.disabled = true;
      btnMove.textContent = "...";

      try {
        const variantId = await getFirstAvailableVariantId(handle);
        const response = await addToCart(variantId, 1);

        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: "wishlist",
              data: {
                source: "product-form-component",
                itemCount: 1,
                sections: response.sections || {},
              },
            },
          })
        );

        const drawer = document.querySelector("cart-drawer-component");
        const drawerOpen = !!document.querySelector("cart-drawer-component dialog[open]");
        if (drawer && !drawerOpen && typeof drawer.open === "function") {
          drawer.open();
        }

        removeHandle(handle);
        showToast("Moved to cart");
      } catch (err) {
        console.error(err);
        showToast("Could not add to cart");
      } finally {
        btnMove.disabled = false;
        btnMove.textContent = "Move to Cart";
      }
      return;
    }

    // Share
    if (btnShare) {
      e.preventDefault();
      const url = getShareUrl();
      const ok = await copyToClipboard(url);
      showToast(ok ? "Wishlist link copied" : "Copy failed");
      return;
    }
  });

  // ESC key to close drawer
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeWishlistDrawer();
    }
  });

  // ---------------------------
  // Boot / events
  // ---------------------------
  const boot = () => {
    importFromUrlOnce();
    check404Fallback();
    sync(document);
    renderWishlistPage();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.addEventListener(EVENT, () => {
    sync(document);
    renderWishlistPage();
    renderDrawerItems();
  });

  document.addEventListener("shopify:section:load", (e) => {
    sync(e.target);
    renderWishlistPage();
  });

  window.DawnWishlist = {
    read, write, toggleHandle, removeHandle,
    sync, renderWishlistPage, openWishlistDrawer, closeWishlistDrawer,
  };
})();
